import React from "react";
import { motion } from "framer-motion";
import { Clock, Tag, CheckCircle2, ChevronRight, Camera } from "lucide-react";
import { Layout } from "@/components/Layout";
import { espacios } from "@/data/index";
import { Espacio } from "@/lib/index";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function Espacios() {
  return (
    <Layout>
      <main className="bg-background">
        {/* Header Section */}
        <section className="relative pt-24 pb-12 overflow-hidden">
          <div className="container mx-auto px-4">
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="max-w-3xl"
            >
              <span className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold tracking-wider uppercase bg-accent/20 text-accent-foreground rounded-full border border-accent/30">
                Experiencias Las Gaviotas
              </span>
              <h1 className="text-5xl md:text-7xl font-semibold mb-6 font-display text-primary tracking-tighter">
                Nuestros <br />
                <span className="italic text-accent-foreground">Espacios Exclusivos</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Descubre los tres pilares de nuestro complejo en Playa El Maviri. 
                Desde gastronomía tradicional hasta estancias de lujo frente al Mar de Cortés.
              </p>
            </motion.div>
          </div>
          
          {/* Decorative Background Element */}
          <div className="absolute top-0 right-0 -z-10 w-1/3 h-full opacity-10">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <path fill="var(--primary)" d="M44.7,-76.4C58.2,-69.2,70,-58.5,77.5,-45.5C85,-32.5,88.3,-17.2,87.3,-2.3C86.3,12.6,81.1,27.1,72.4,39.6C63.7,52.1,51.6,62.6,38.2,69.5C24.8,76.4,10.2,79.7,-4.1,86.8C-18.4,93.9,-32.4,104.8,-45.3,101.4C-58.2,98,-70,80.3,-77.3,64.2C-84.6,48.1,-87.4,33.6,-88.7,19.3C-90,5.1,-89.7,-8.9,-85.4,-21.9C-81.1,-34.9,-72.7,-47,-61.8,-55.4C-50.9,-63.8,-37.4,-68.5,-24.5,-76C-11.6,-83.5,0.7,-93.8,13.7,-91.7C26.7,-89.6,40.3,-75.1,44.7,-76.4Z" transform="translate(100 100)" />
            </svg>
          </div>
        </section>

        {/* Spaces Detail Sections */}
        <div className="pb-24 space-y-32">
          {espacios.map((espacio, index) => (
            <EspacioDetail key={espacio.id} espacio={espacio} index={index} />
          ))}
        </div>

        {/* Call to Action */}
        <section className="container mx-auto px-4 pb-24">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="bg-primary text-primary-foreground rounded-3xl p-12 md:p-20 text-center relative overflow-hidden"
          >
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-semibold mb-6">¿Listo para vivir la experiencia?</h2>
              <p className="text-lg opacity-80 mb-10 max-w-2xl mx-auto">
                Ya sea una cena inolvidable o un descanso reparador, en Las Gaviotas tenemos el lugar perfecto para ti.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="#contacto" 
                  className="bg-accent text-accent-foreground px-8 py-4 rounded-full font-semibold hover:bg-accent/90 transition-all flex items-center gap-2 group"
                >
                  Reservar Ahora
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a 
                  href="/menu" 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all"
                >
                  Ver Menú
                </a>
              </div>
            </div>
            
            {/* Decorative background circle */}
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
            <div className="absolute -top-24 -left-24 w-64 h-64 bg-accent/10 rounded-full blur-2xl" />
          </motion.div>
        </section>
      </main>
    </Layout>
  );
}

function EspacioDetail({ espacio, index }: { espacio: Espacio; index: number }) {
  const isEven = index % 2 === 0;

  return (
    <section className="container mx-auto px-4">
      <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}>
        {/* Content Side */}
        <div className="w-full lg:w-1/2 space-y-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="space-y-6"
          >
            <motion.div variants={staggerItem}>
              <h2 className="text-4xl md:text-5xl font-semibold text-primary font-display mb-2">
                {espacio.nombre}
              </h2>
              <p className="text-xl text-accent-foreground font-medium italic">
                {espacio.tagline}
              </p>
            </motion.div>

            <motion.p 
              variants={staggerItem}
              className="text-lg text-muted-foreground leading-relaxed"
            >
              {espacio.descripcionLarga}
            </motion.p>

            <motion.div variants={staggerItem} className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              {espacio.amenidades.map((amenidad) => (
                <div key={amenidad} className="flex items-center gap-3 text-foreground/80">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-secondary-foreground" />
                  </div>
                  <span className="text-sm font-medium">{amenidad}</span>
                </div>
              ))}
            </motion.div>

            <motion.div 
              variants={staggerItem}
              className="p-6 bg-secondary/50 rounded-2xl border border-secondary flex flex-wrap gap-8"
            >
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-accent-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Horario</p>
                  <p className="text-sm font-semibold">{espacio.horario}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Tag className="w-5 h-5 text-accent-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Rango de Precio</p>
                  <p className="text-sm font-semibold">{espacio.rangoPrecio}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Visual Side (Gallery) */}
        <div className="w-full lg:w-1/2">
          <motion.div
            initial={{ opacity: 0, x: isEven ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.smooth}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl group">
              <img 
                src={espacio.imagenPrincipal} 
                alt={espacio.nombre} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-6 right-6">
                <div className="bg-white/20 backdrop-blur-md border border-white/30 p-2 rounded-full">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>

            {/* Thumbnails Gallery */}
            <div className="grid grid-cols-3 gap-4">
              {espacio.galeria.map((img, i) => (
                <motion.div 
                  key={i}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="aspect-square rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                >
                  <img 
                    src={img} 
                    alt={`${espacio.nombre} galeria ${i + 1}`} 
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
